import { useState, useEffect } from 'react'
import api from '../services/api'
import { t, marca, tDato } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Muestra la foto del paciente de forma SEGURA:
//  - La pide al endpoint autenticado /pacientes/:id/foto con el JWT (responseType blob),
//    NUNCA como <img src="/api/..."> (eso no llevaría el token y dejaría una URL pública).
//  - Crea un objectURL temporal en memoria y lo libera al desmontar/cambiar.
//  - Fallback a las iniciales si el paciente no tiene foto (404) o hay error.
//  - Al pulsar la foto, se abre en grande (lightbox). Reutiliza el mismo blob.
// Props: pacienteId, nombre, apellidos, size (px), tieneFoto (si es false, ni la pide).
//   mini: para LISTADOS (buscador). Usa /foto-mini, que NO registra en la auditoría de accesos a la
//   foto — ese log audita quién consulta la ficha de un paciente concreto, y pintar 50 caras en una
//   búsqueda lo llenaría de ruido, tapando justo lo que debe detectar. Además desactiva el lightbox
//   (en una lista, el clic abre la ficha, no amplía la foto).
//   ampliable: vuelve a permitir el lightbox EN modo mini. Existe para listas cortas en las que la
//   foto es parte de lo que hay que mirar (el repaso de urgencias: reconocer a quién estás quitando
//   de la lista). Lo que decide si se audita o no es el ENDPOINT, no el que la foto se pueda
//   ampliar, así que ampliar la miniatura no salta ningún control: sigue sin registrar en el log y
//   sigue siendo la misma imagen que ya se ha descargado.
//   onSinFoto: avisa de que ESTA foto no se ha podido pintar. `tiene_foto` en la base no siempre
//   dice la verdad —lo pone un escaneo de la carpeta de fotos del otro programa, y se queda en TRUE
//   cuando el fichero ya no esta—, asi que habia pacientes sin cara en pantalla y sin el aviso de
//   "SIN FOTO". Este callback lo dice desde el unico sitio que lo sabe con certeza: el que ha
//   intentado descargarla.
//   noQuiereFoto (t272, 16-oct-2026): el paciente NO QUIERE FOTO. Es la marca de su ficha
//   (`pacientes.foto_no_deseada`): la que pone el enlace «Este paciente no quiere foto (no volver a
//   pedir)» de la ficha y de la cita, y el «No» firmado en el documento de la foto. Quien pinta la cara
//   la pasa de la fila que ya trae (`foto_no_deseada` en las de pacientes, `paciente_foto_no_deseada`
//   en las de citas). El dueño: «en su avatar donde salen las iniciales salga una especie de cámara con
//   opacidad y encima una señal de prohibido, para saber que esa persona no quiere foto».
//     · sin foto y con la marca → la CÁMARA TACHADA en vez de las iniciales («No quiere foto»);
//     · con una foto guardada → su foto, como siempre (se la hicieron con permiso, antes de decir que
//       no), con la misma señal en pequeño en la esquina, discreta;
//     · sin la marca → todo exactamente como antes.

// LA CÁMARA TACHADA. La cámara de la casa —la misma silueta que `camera` de components/ui— con poca
// opacidad y, ENCIMA (se pinta después), la señal de prohibido: un círculo con su barra en diagonal, en
// el rojo de aviso de la casa. `--danger-tinta` es el rojo que se lee en oscuro (index.css lo voltea
// en html.dark); en claro vale lo mismo que `--danger`. SVG en línea, sin librerías: el grosor va en
// las unidades del dibujo, así que crece con el avatar y con el tamaño de letra del perfil.
const NO_QUIERE_FOTO = marca('components.FotoPaciente.no_quiere_foto', 'No quiere foto')
function CamaraTachada({ px, conCamara = true }) {
  return (
    <svg data-camara-tachada="" viewBox="0 0 24 24" width={px} height={px} aria-hidden="true" focusable="false"
      style={{ display: 'block', flexShrink: 0, overflow: 'visible' }}>
      {conCamara && (
        <g data-camara="" transform="translate(5.4 5.4) scale(0.55)" fill="none" stroke="currentColor"
          strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" opacity="0.45">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </g>
      )}
      <g data-prohibido="" fill="none" strokeWidth="2" strokeLinecap="round" style={{ stroke: 'var(--danger-tinta, var(--danger))' }}>
        <circle cx="12" cy="12" r="10" />
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
      </g>
    </svg>
  )
}

export default function FotoPaciente({ pacienteId, nombre, apellidos, size = 44, tieneFoto, mini, ampliable, onSinFoto, noQuiereFoto }) {
  const [url, setUrl] = useState(null)
  const [zoom, setZoom] = useState(false)

  useEffect(() => {
    let cancelado = false
    let objUrl = null
    setUrl(null)
    setZoom(false)
    if (!pacienteId) return
    if (tieneFoto === false) { if (onSinFoto) onSinFoto(pacienteId); return }
    api.get(`/pacientes/${pacienteId}/${mini ? 'foto-mini' : 'foto'}`, { responseType: 'blob' })
      .then(r => {
        if (cancelado) return
        objUrl = URL.createObjectURL(r.data)
        setUrl(objUrl)
      })
      .catch(() => { if (!cancelado && onSinFoto) onSinFoto(pacienteId) })   // 404 sin foto → iniciales Y aviso
    return () => { cancelado = true; if (objUrl) URL.revokeObjectURL(objUrl) }
  }, [pacienteId, tieneFoto, mini])

  // Cerrar el zoom con Escape
  useEffect(() => {
    if (!zoom) return
    const onKey = (e) => { if (e.key === 'Escape') setZoom(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [zoom])

  const ini = `${(nombre || '?')[0] || ''}${(apellidos || '?')[0] || ''}`.toUpperCase()
  const base = { width: size, height: size, borderRadius: '50%', flexShrink: 0 }
  const nombreCompleto = `${nombre || ''} ${apellidos || ''}`.trim()

  // Sin foto → avatar de iniciales (no clicable)
  if (!url) {
    // Y SI NO QUIERE FOTO, NO SON LAS INICIALES: es la cámara tachada, sobre el gris de la superficie
    // (no sobre el color de la clínica, que puede ser rojo y se comería la señal). Con su texto al pasar
    // por encima y para el lector de pantalla.
    if (noQuiereFoto === true) {
      return (
        <div data-no-quiere-foto="" role="img" aria-label={tDato(NO_QUIERE_FOTO)} title={tDato(NO_QUIERE_FOTO)}
          style={{ ...base, boxSizing: 'border-box', background: 'var(--surface-2)', color: 'var(--text-secondary)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CamaraTachada px={Math.round(size * 0.7)} />
        </div>
      )
    }
    return (
      <div style={{ ...base, background: 'var(--primary)', color: 'var(--sobre-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: Math.round(size * 0.36) }}>
        {ini}
      </div>
    )
  }

  // CON FOTO GUARDADA Y LA MARCA PUESTA: la foto se queda —se la hicieron con permiso, antes de decir
  // que no— y lleva en la esquina la misma señal en pequeño, para que quien la vea no le haga otra. Sin
  // la marca, la foto sale exactamente como siempre, sin envoltorio.
  const chip = Math.max(12, Math.round(size * 0.32))
  const conDistintivo = (img) => (noQuiereFoto !== true ? img : (
    <span data-no-quiere-foto="con-foto" style={{ position: 'relative', display: 'inline-flex', width: size, height: size, flexShrink: 0 }}>
      {img}
      <span role="img" aria-label={tDato(NO_QUIERE_FOTO)} title={t('components.FotoPaciente.esta_se_la_hicieron_antes', '{{NO_QUIERE_FOTO}} (esta se la hicieron antes)', { NO_QUIERE_FOTO: tDato(NO_QUIERE_FOTO) })}
        style={{ position: 'absolute', right: -1, bottom: -1, width: chip, height: chip, borderRadius: '50%', background: 'var(--surface)', color: 'var(--text-secondary)', boxShadow: '0 0 0 1.5px var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CamaraTachada px={chip - 2} conCamara={chip >= 18} />
      </span>
    </span>
  ))

  // En un listado la foto no se amplía: el clic debe abrir la ficha (lo maneja la fila). Salvo que
  // la lista pida explícitamente lo contrario con `ampliable`.
  if (mini && !ampliable) return conDistintivo(<img src={url} alt={nombreCompleto} title={nombreCompleto}
    style={{ ...base, objectFit: 'cover', border: '1px solid var(--border)' }} />)

  return (
    <>
      {conDistintivo(<img src={url} alt={nombreCompleto} title={t('components.FotoPaciente.pulsa_para_ampliar', 'Pulsa para ampliar')}
        onClick={(e) => { e.stopPropagation(); setZoom(true) }}
        style={{ ...base, objectFit: 'cover', border: '1px solid var(--border)', cursor: 'zoom-in' }} />)}
      {zoom && (
        <div className="modal-safe visor-oscuro" onClick={(e) => { e.stopPropagation(); setZoom(false) }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: 'max(16px, var(--sa-abajo))', cursor: 'zoom-out' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <img src={url} alt={nombreCompleto}
              style={{ maxWidth: 'calc(92vw / var(--z, 1))', maxHeight: 'calc(82vh / var(--z, 1))', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.5)', objectFit: 'contain' }} />
            {nombreCompleto && <div style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>{nombreCompleto}</div>}
          </div>
          <button onClick={(e) => { e.stopPropagation(); setZoom(false) }} title={t('components.FotoPaciente.cerrar_esc', 'Cerrar (Esc)')}
            style={{ position: 'fixed', top: '18px', right: '22px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', fontSize: '22px', cursor: 'pointer' }}>×</button>
        </div>
      )}
    </>
  )
}
